import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  MapPin, Upload, X, Check, ChevronRight, ChevronLeft, 
  Building, Phone, Clock, CreditCard, Heart, Briefcase, Camera,
  Globe, AlertCircle, Loader2, Save, ArrowLeft
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { toast } from 'sonner';
// Use centralized API service
import { 
  categoriesApi, 
  municipiosApi, 
  businessesApi,
  territoriesApi,
  api 
} from '../services/api';
import AddressInput from '../components/AddressInput';
import CepInput, { stripCep } from '../components/CepInput';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';
import EspecializacoesAutocomplete from '../components/EspecializacoesAutocomplete';
import { api as apiClient } from '../services/api';

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps, stepLabels, onStepClick }) => (
  <div className="mb-8">
    <div className="overflow-x-auto pb-2 -mx-4 px-4">
      <div className="flex items-center min-w-max">
        {Array.from({ length: totalSteps }, (_, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center flex-shrink-0">
              <button
                type="button"
                onClick={() => onStepClick && onStepClick(i + 1)}
                className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all cursor-pointer hover:scale-110 ${
                  i + 1 <= currentStep
                    ? 'bg-[var(--primary)] text-white'
                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}
                data-testid={`step-${i + 1}`}
              >
                {i + 1 <= currentStep - 1 ? <Check className="w-4 h-4 md:w-5 md:h-5" /> : i + 1}
              </button>
              <span className="text-[10px] md:text-xs mt-1 text-center w-16 md:w-20 truncate text-[var(--text-secondary)]">
                {stepLabels[i]}
              </span>
            </div>
            {i < totalSteps - 1 && (
              <div
                className={`w-4 md:w-6 h-0.5 mx-0.5 md:mx-1 rounded flex-shrink-0 ${
                  i + 1 < currentStep ? 'bg-[var(--primary)]' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
    <div className="text-center mt-2 text-sm font-medium text-[var(--primary)] md:hidden">
      Etapa {currentStep} de {totalSteps}: {stepLabels[currentStep - 1]}
    </div>
  </div>
);

// Checkbox item component
const CheckboxItem = ({ id, label, checked, onChange, icon }) => (
  <label
    htmlFor={id}
    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
      checked
        ? 'border-[var(--primary)] bg-[var(--primary)]/5'
        : 'border-[var(--border)] hover:border-[var(--primary)]/50'
    }`}
  >
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
      className="w-5 h-5 rounded border-[var(--border)] text-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
    />
    {icon && <span className="text-lg">{icon}</span>}
    <span className="text-sm font-medium">{label}</span>
  </label>
);

// Input component
const FormInput = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      {...props}
      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
    />
  </div>
);

// Textarea component
const FormTextarea = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      {...props}
      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
    />
  </div>
);

// Step labels
const stepLabels = [
  'Básico', 'Econômico', 'Presença', 'Contato', 'Horários', 'Local',
  'Comodidades', 'Pagamentos', 'Diferenciais', 'Social', 'Mídia'
];

export default function EditarNegocio() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, token } = useAuth();
  const { location } = useLocation();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [availableTags, setAvailableTags] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [businessNotFound, setBusinessNotFound] = useState(false);
  const [territorios, setTerritorios] = useState([]);
  const [bairrosTerritorios, setBairrosTerritorios] = useState([]);

  // Business form state
  const [businessForm, setBusinessForm] = useState({
    // Informações Básicas
    cnpj_cpf: '',
    nome: '',
    nome_fantasia: '',
    descricao_curta: '',
    descricao: '',
    categoria: '',
    categorias_secundarias: [],
    tags: [],
    tags_controladas_ids: [],
    tags_livres_pendentes: [],
    subcategoria: '',
    ano_fundacao: '',

    // Perfil Econômico
    tem_cnpj: false,
    tipo_empresa: '',
    situacao_renda: '',
    situacao_ocupacao: '',
    colaboradores_diretos: '',
    colaboradores_indiretos: '',
    contratacao_sazonal: false,
    periodos_sazonais: '',

    // Localização
    cep: '',
    rua: '',
    numero: '',
    complemento: '',
    endereco: '',
    bairro: '',
    municipio_id: '',
    territory_id: '',
    is_sede: null,
    distrito_id: '',
    latitude: 0,
    longitude: 0,

    // Como Atende aos Clientes
    loja_fisica: false,
    atendimento_no_meu_local: false,
    atendimento_no_local_cliente: false,
    atendimento_com_agendamento: false,
    atendimento_online: false,
    delivery: false,
    retirada_no_local: false,
    entrega_combinada: false,
    itinerante: false,
    ocultar_endereco: false,
    area_cobertura: '',

    // Contato & Digital
    telefone_fixo: '',
    whatsapp: '',
    email: '',
    site: '',
    loja_online: '',
    instagram: '',

    // Funcionamento
    horarios: [
      { dia: 'segunda', abre: '08:00', fecha: '18:00', fechado: false },
      { dia: 'terca', abre: '08:00', fecha: '18:00', fechado: false },
      { dia: 'quarta', abre: '08:00', fecha: '18:00', fechado: false },
      { dia: 'quinta', abre: '08:00', fecha: '18:00', fechado: false },
      { dia: 'sexta', abre: '08:00', fecha: '18:00', fechado: false },
      { dia: 'sabado', abre: '08:00', fecha: '12:00', fechado: false },
      { dia: 'domingo', abre: '', fecha: '', fechado: true }
    ],
    horarios_especiais: '',
    apenas_agendamento: false,
    emergencial_24h: false,

    // Serviços
    entrega: false,
    atende_domicilio: false,
    promocao: false,
    link_delivery: '',
    faixa_preco: '',

    // Comodidades
    acessibilidade: false,
    banheiro_publico: false,
    estacionamento: '',
    wifi_gratis: false,
    pet_friendly: false,
    espaco_kids: false,
    ar_condicionado: false,
    area_externa: false,
    seguranca: false,

    // Pagamentos
    aceita_dinheiro: true,
    aceita_cartao_credito: false,
    aceita_cartao_debito: false,
    aceita_pix: false,
    aceita_convenios: false,
    convenios_descricao: '',

    // Diferenciais
    producao_artesanal: false,
    produtos_sustentaveis: false,
    certificacoes: [],
    cooperativa: false,
    empresa_familiar: false,

    // Social & Impacto
    projetos_sociais: false,
    emprega_comunidade: false,
    trabalha_inclusao: false,
    apoia_cultura: false,
    iniciativas_sustentaveis: [],

    // Contratação
    esta_contratando: false,
    vagas: [],

    // Mídia
    avatar_url: '',
    images: []
  });

  // Load categories and municipios
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await categoriesApi.list();
        // Filtra arquivadas mantendo a atual do negócio (caso ele ainda
        // esteja numa v1 que foi bloqueada). Assim o select não fica em
        // branco e o dono pode migrar pra v2 no próprio dropdown.
        const currentCatId = businessForm?.categoria;
        const ativas = (Array.isArray(data) ? data : []).filter(
          (c) => !c?.bloqueada_para_novos_cadastros || c?.id === currentCatId
        );
        setCategories(ativas);
      } catch (err) {
        console.error('Erro ao carregar categorias:', err);
      }
    };
    const loadMunicipios = async () => {
      try {
        const data = await municipiosApi.list();
        setMunicipios(data);
      } catch (err) {
        console.error('Erro ao carregar municípios:', err);
      }
    };
    loadCategories();
    loadMunicipios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessForm.categoria]);

  // Load territories when municipio changes
  const loadTerritorios = async (municipioId) => {
    try {
      const data = await territoriesApi.list({ municipio_id: municipioId });
      setTerritorios(data);
    } catch (error) {
      console.error('Erro ao carregar territórios:', error);
    }
  };

  const loadBairrosTerritorios = async (parentId) => {
    try {
      const data = await territoriesApi.bairros(parentId);
      setBairrosTerritorios(data);
    } catch (error) {
      console.error('Erro ao carregar bairros:', error);
    }
  };

  const handleMunicipioChange = (municipioId) => {
    setBusinessForm(prev => ({
      ...prev,
      municipio_id: municipioId,
      territory_id: '',
      is_sede: null,
      distrito_id: '',
      bairro: ''
    }));
    setTerritorios([]);
    setBairrosTerritorios([]);
    if (municipioId) {
      loadTerritorios(municipioId);
    }
  };

  const handleTerritoryTypeChange = (isSede) => {
    setBusinessForm(prev => ({
      ...prev,
      is_sede: isSede,
      distrito_id: '',
      territory_id: '',
      bairro: ''
    }));
    setBairrosTerritorios([]);
    if (isSede) {
      const sede = territorios.find(t => t.tipo === 'sede');
      if (sede) {
        loadBairrosTerritorios(sede.id);
      }
    }
  };

  const handleDistritoChange = (distritoId) => {
    setBusinessForm(prev => ({
      ...prev,
      distrito_id: distritoId,
      territory_id: '',
      bairro: ''
    }));
    setBairrosTerritorios([]);
    if (distritoId) {
      loadBairrosTerritorios(distritoId);
    }
  };

  const handleTerritorySelect = (territoryId) => {
    const territory = bairrosTerritorios.find(t => t.id === territoryId);
    setBusinessForm(prev => ({
      ...prev,
      territory_id: territoryId,
      bairro: territory?.nome || prev.bairro
    }));
  };

  // Load business data
  useEffect(() => {
    const loadBusiness = async () => {
      console.log('LoadBusiness called - id:', id, 'token exists:', !!token);
      
      if (!id) {
        console.error('No business ID provided');
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching business data for:', id);
        
        // Try v2 API first, fallback to legacy
        let business;
        business = await businessesApi.get(id);
        
        console.log('Business data received:', business);
        
        // Map the business data to the form
        setBusinessForm(prev => ({
          ...prev,
          cnpj_cpf: business.cnpj_cpf || business.cnpj || '',
          nome: business.nome || '',
          nome_fantasia: business.nome_fantasia || '',
          descricao_curta: business.descricao_curta || '',
          descricao: business.descricao || '',
          categoria: business.categoria || '',
          categorias_secundarias: business.categorias_secundarias || [],
          tags: business.tags || [],
          tags_controladas_ids: business.tags_controladas_ids || [],
          tags_livres_pendentes: business.tags_livres_pendentes || [],
          subcategoria: business.subcategoria || '',
          ano_fundacao: business.ano_fundacao || '',
          
          // Perfil Econômico
          tem_cnpj: business.tem_cnpj || false,
          tipo_empresa: business.tipo_empresa || '',
          situacao_renda: business.situacao_renda || '',
          situacao_ocupacao: business.situacao_ocupacao || '',
          colaboradores_diretos: business.colaboradores_diretos || '',
          colaboradores_indiretos: business.colaboradores_indiretos || '',
          contratacao_sazonal: business.contratacao_sazonal || false,
          periodos_sazonais: business.periodos_sazonais || '',
          
          endereco: business.endereco || '',
          cep: business.cep || '',
          rua: business.rua || '',
          numero: business.numero || '',
          complemento: business.complemento || '',
          bairro: business.bairro || '',
          municipio_id: business.municipio_id || '',
          territory_id: business.territory_id || '',
          is_sede: business.is_sede ?? null,
          distrito_id: business.distrito_id || '',
          latitude: business.latitude || business.coordenadas?.latitude || 0,
          longitude: business.longitude || business.coordenadas?.longitude || 0,
          
          loja_fisica: business.loja_fisica || business.tipo_presenca?.loja_fisica || false,
          atendimento_no_meu_local: business.atendimento_no_meu_local || business.atendo_em_casa || false,
          atendimento_no_local_cliente: business.atendimento_no_local_cliente || false,
          atendimento_com_agendamento: business.atendimento_com_agendamento || false,
          atendimento_online: business.atendimento_online || business.apenas_online || false,
          delivery: business.delivery || false,
          retirada_no_local: business.retirada_no_local || false,
          entrega_combinada: business.entrega_combinada || false,
          itinerante: business.itinerante || business.tipo_presenca?.itinerante || false,
          ocultar_endereco: business.ocultar_endereco || false,
          area_cobertura: business.area_cobertura || '',
          
          telefone_fixo: business.telefone_fixo || business.telefone || '',
          whatsapp: business.whatsapp || '',
          email: business.email || '',
          site: business.site || '',
          loja_online: business.loja_online || '',
          instagram: business.instagram || '',
          
          horarios: business.horarios || prev.horarios,
          horarios_especiais: business.horarios_especiais || '',
          apenas_agendamento: business.apenas_agendamento || false,
          emergencial_24h: business.emergencial_24h || false,
          
          entrega: business.entrega || business.faz_entrega || false,
          atende_domicilio: business.atende_domicilio || false,
          promocao: business.promocao || business.tem_promocao || false,
          link_delivery: business.link_delivery || '',
          faixa_preco: business.faixa_preco || '',
          
          acessibilidade: business.acessibilidade || business.amenities?.acessibilidade || false,
          banheiro_publico: business.banheiro_publico || business.amenities?.banheiro_publico || false,
          estacionamento: business.estacionamento || business.amenities?.estacionamento || '',
          wifi_gratis: business.wifi_gratis || business.amenities?.wifi_gratis || false,
          pet_friendly: business.pet_friendly || business.amenities?.pet_friendly || false,
          espaco_kids: business.espaco_kids || business.amenities?.espaco_kids || false,
          ar_condicionado: business.ar_condicionado || business.amenities?.ar_condicionado || false,
          area_externa: business.area_externa || business.amenities?.area_externa || false,
          seguranca: business.seguranca || business.amenities?.seguranca || false,
          
          aceita_dinheiro: business.aceita_dinheiro ?? business.pagamentos?.dinheiro ?? true,
          aceita_cartao_credito: business.aceita_cartao_credito ?? business.pagamentos?.cartao_credito ?? false,
          aceita_cartao_debito: business.aceita_cartao_debito ?? business.pagamentos?.cartao_debito ?? false,
          aceita_pix: business.aceita_pix ?? business.pagamentos?.pix ?? false,
          aceita_convenios: business.aceita_convenios ?? business.pagamentos?.convenios ?? false,
          convenios_descricao: business.convenios_descricao || '',
          
          producao_artesanal: business.producao_artesanal || business.diferenciais?.producao_artesanal || false,
          produtos_sustentaveis: business.produtos_sustentaveis || business.diferenciais?.produtos_sustentaveis || false,
          certificacoes: business.certificacoes || business.diferenciais?.certificacoes || [],
          cooperativa: business.cooperativa || business.diferenciais?.cooperativa || false,
          empresa_familiar: business.empresa_familiar || business.diferenciais?.empresa_familiar || false,
          
          projetos_sociais: business.projetos_sociais || business.impacto_social?.projetos_sociais || false,
          emprega_comunidade: business.emprega_comunidade || business.impacto_social?.emprega_comunidade || false,
          trabalha_inclusao: business.trabalha_inclusao || business.impacto_social?.trabalha_inclusao || false,
          apoia_cultura: business.apoia_cultura || business.impacto_social?.apoia_cultura || false,
          iniciativas_sustentaveis: business.iniciativas_sustentaveis || business.impacto_social?.iniciativas_sustentaveis || [],
          
          esta_contratando: business.esta_contratando || false,
          vagas: business.vagas || [],
          
          // Compat: hidrata a partir de qualquer um dos formatos possíveis.
          // Prioridade: legacy (avatar_url/images) > moderno (imagens.logo/galeria).
          // Sem isso, edições posteriores enviavam '' e [] apagando as fotos.
          avatar_url: business.avatar_url
            || business.avatar
            || business.imagens?.logo?.medium
            || business.imagens?.logo?.large
            || business.imagens?.logo?.thumb
            || '',
          images: (business.images && business.images.length ? business.images : null)
            || (business.galeria && business.galeria.length ? business.galeria : null)
            || (Array.isArray(business.imagens?.galeria)
                ? business.imagens.galeria
                    .map((it) => (typeof it === 'string' ? it : (it?.medium || it?.large || it?.thumb || null)))
                    .filter(Boolean)
                : [])
            || [],
          imagens: business.imagens || {},  // novo: estrutura {capa, logo, galeria, banner}
        }));

        // Load subcategories if category exists
        if (business.categoria) {
          try {
            const subcats = await categoriesApi.subcategories(business.categoria);
            setSubcategorias(subcats);
            const tags = await categoriesApi.tags(business.categoria);
            setAvailableTags(tags);
          } catch (e) {
            console.error('Erro ao carregar subcategorias/tags:', e);
          }
        }

        // Load territories if municipio exists
        if (business.municipio_id) {
          try {
            const terrs = await territoriesApi.list({ municipio_id: business.municipio_id });
            setTerritorios(terrs);
            // Load bairros for the correct parent
            const parentId = business.distrito_id || terrs.find(t => t.tipo === 'sede')?.id;
            if (parentId) {
              const bairros = await territoriesApi.bairros(parentId);
              setBairrosTerritorios(bairros);
            }
          } catch (e) {
            console.error('Erro ao carregar territórios:', e);
          }
        }
        
      } catch (err) {
        console.error('Erro ao carregar negócio:', err);
        if (err.response?.status === 404) {
          setBusinessNotFound(true);
        }
        toast.error('Erro ao carregar dados do negócio');
      } finally {
        setLoading(false);
      }
    };
    
    loadBusiness();
  }, [id, token]);

  // Load subcategories when category changes
  const handleCategoryChange = async (categoryId) => {
    setBusinessForm(prev => ({ ...prev, categoria: categoryId, subcategoria: '', tags: [], tags_controladas_ids: [], tags_livres_pendentes: [] }));
    if (categoryId) {
      try {
        const [subcats, tags] = await Promise.all([
          categoriesApi.subcategories(categoryId),
          categoriesApi.tags(categoryId)
        ]);
        setSubcategorias(subcats);
        setAvailableTags(tags);
      } catch (e) {
        console.error('Erro ao carregar subcategorias:', e);
      }
    } else {
      setSubcategorias([]);
      setAvailableTags({});
    }
  };

  // Handle image upload
  const handleImageUpload = async (e, isAvatar = false) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Imagem muito grande. Máximo 5MB.');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/admin/upload/image', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Salva URL relativa — `resolveBackendImage` resolve o host na exibição.
        const imageUrl = response.data.url;
        if (isAvatar) {
          setBusinessForm(prev => ({ ...prev, avatar_url: imageUrl }));
        } else {
          setBusinessForm(prev => ({ ...prev, images: [...prev.images, imageUrl] }));
        }
        toast.success('Imagem enviada com sucesso!');
      }
    } catch (err) {
      console.error('Erro no upload:', err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail.slice(0, 200) : 'Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  // Remove image
  const removeImage = (index) => {
    setBusinessForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Save business
  const handleSave = async () => {
    // Validar subcategoria obrigatória quando há subcategorias disponíveis
    if (businessForm.categoria && subcategorias.length > 0 && !businessForm.subcategoria) {
      toast.error('Selecione a subcategoria do seu negócio.');
      document.querySelector('[data-testid="edit-business-subcategoria-select"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSaving(true);
    try {
      // Prepare data for API - must match BusinessCreate model in backend
      const updateData = {
        // Informações Básicas
        cnpj_cpf: businessForm.cnpj_cpf || '',
        nome: businessForm.nome,
        nome_fantasia: businessForm.nome_fantasia || null,
        descricao_curta: businessForm.descricao_curta || '',
        descricao: businessForm.descricao || '',
        categoria: businessForm.categoria,
        categorias_secundarias: businessForm.categorias_secundarias || [],
        tags: businessForm.tags || [],
        tags_controladas_ids: businessForm.tags_controladas_ids || [],
        tags_livres_pendentes: businessForm.tags_livres_pendentes || [],
        subcategoria: businessForm.subcategoria || null,
        ano_fundacao: businessForm.ano_fundacao ? parseInt(businessForm.ano_fundacao) : null,
        
        // Localização
        endereco: [businessForm.rua, businessForm.numero ? `nº ${businessForm.numero}` : '', businessForm.complemento].filter(Boolean).join(', ') || businessForm.endereco || '',
        cep: stripCep(businessForm.cep),
        rua: businessForm.rua || '',
        numero: businessForm.numero || '',
        complemento: businessForm.complemento || '',
        bairro: businessForm.bairro || '',
        municipio_id: businessForm.municipio_id || null,
        territory_id: businessForm.territory_id || null,
        is_sede: businessForm.is_sede,
        distrito_id: businessForm.distrito_id || null,
        latitude: businessForm.latitude || 0,
        longitude: businessForm.longitude || 0,
        
        // Como Atende aos Clientes
        loja_fisica: businessForm.loja_fisica || false,
        atendimento_no_meu_local: businessForm.atendimento_no_meu_local || false,
        atendimento_no_local_cliente: businessForm.atendimento_no_local_cliente || false,
        atendimento_com_agendamento: businessForm.atendimento_com_agendamento || false,
        atendimento_online: businessForm.atendimento_online || false,
        delivery: businessForm.delivery || false,
        retirada_no_local: businessForm.retirada_no_local || false,
        entrega_combinada: businessForm.entrega_combinada || false,
        itinerante: businessForm.itinerante || false,
        ocultar_endereco: businessForm.ocultar_endereco || false,
        area_cobertura: businessForm.area_cobertura || null,
        
        // Contato & Digital
        telefone: businessForm.telefone_fixo || null,
        telefone_fixo: businessForm.telefone_fixo || null,
        whatsapp: businessForm.whatsapp || '',
        email: businessForm.email || null,
        site: businessForm.site || null,
        loja_online: businessForm.loja_online || null,
        instagram: businessForm.instagram || null,
        
        // Funcionamento
        horarios: businessForm.horarios || [],
        horarios_especiais: businessForm.horarios_especiais || null,
        apenas_agendamento: businessForm.apenas_agendamento || false,
        emergencial_24h: businessForm.emergencial_24h || false,
        
        // Serviços
        entrega: businessForm.entrega || false,
        atende_domicilio: businessForm.atende_domicilio || false,
        promocao: businessForm.promocao || false,
        link_delivery: businessForm.link_delivery || null,
        faixa_preco: businessForm.faixa_preco || null,
        
        // Comodidades (flat fields, not nested in amenities)
        acessibilidade: businessForm.acessibilidade || false,
        banheiro_publico: businessForm.banheiro_publico || false,
        estacionamento: businessForm.estacionamento || null,
        wifi_gratis: businessForm.wifi_gratis || false,
        pet_friendly: businessForm.pet_friendly || false,
        espaco_kids: businessForm.espaco_kids || false,
        ar_condicionado: businessForm.ar_condicionado || false,
        area_externa: businessForm.area_externa || false,
        seguranca: businessForm.seguranca || false,
        
        // Pagamentos (flat fields, not nested)
        aceita_dinheiro: businessForm.aceita_dinheiro ?? true,
        aceita_cartao_credito: businessForm.aceita_cartao_credito || false,
        aceita_cartao_debito: businessForm.aceita_cartao_debito || false,
        aceita_pix: businessForm.aceita_pix || false,
        aceita_convenios: businessForm.aceita_convenios || false,
        convenios_descricao: businessForm.convenios_descricao || null,
        
        // Diferenciais (flat fields)
        producao_artesanal: businessForm.producao_artesanal || false,
        produtos_sustentaveis: businessForm.produtos_sustentaveis || false,
        certificacoes: businessForm.certificacoes || [],
        cooperativa: businessForm.cooperativa || false,
        empresa_familiar: businessForm.empresa_familiar || false,
        
        // Social & Impacto (flat fields)
        projetos_sociais: businessForm.projetos_sociais || false,
        emprega_comunidade: businessForm.emprega_comunidade || false,
        trabalha_inclusao: businessForm.trabalha_inclusao || false,
        apoia_cultura: businessForm.apoia_cultura || false,
        iniciativas_sustentaveis: businessForm.iniciativas_sustentaveis || [],
        
        // Contratação
        esta_contratando: businessForm.esta_contratando || false,
        vagas: businessForm.vagas || [],

        // Mídia: NÃO enviamos avatar_url/images no PUT. Essas fotos são
        // geridas EXCLUSIVAMENTE pelo endpoint /upload/imagem, que já
        // grava `imagens.logo`/`imagens.galeria` e o mirror avatar_url no
        // banco em tempo real. Enviar aqui causa o backend a reconstruir
        // `imagens.*` a partir do businessForm (que fica stale quando o
        // usuário adiciona fotos após carregar o formulário) — e assim
        // apagava fotos recém-adicionadas na galeria. Bug reportado em prod.
      };

      const res = await api.put(`/businesses/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const bizData = res.data;
      const modStatus = bizData?.moderation_status;

      if (modStatus === 'pending_review') {
        toast.success('Negócio atualizado! Suas alterações estão em análise pela equipe de moderação.');
      } else if (modStatus === 'rejected') {
        toast.error('Atualização rejeitada: ' + (bizData?.moderation_reason || 'Conteúdo inadequado'));
        setSaving(false);
        return;
      } else {
        toast.success('Negócio atualizado com sucesso!');
      }
      navigate('/perfil?tab=negocios');
    } catch (err) {
      console.error('Erro ao salvar:', err);
      toast.error(err.response?.data?.detail || 'Erro ao salvar negócio');
    } finally {
      setSaving(false);
    }
  };

  // Navigate steps
  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 11));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Acesso Restrito</h2>
          <p className="text-[var(--text-secondary)] mb-4">Você precisa estar logado para editar um negócio.</p>
          <button
            onClick={() => navigate('/perfil')}
            className="bg-[var(--primary)] text-white px-6 py-2 rounded-lg font-medium"
          >
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">Carregando dados do negócio...</p>
        </div>
      </div>
    );
  }

  if (businessNotFound) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Negócio não encontrado</h2>
          <p className="text-[var(--text-secondary)] mb-4">O negócio que você está tentando editar não existe ou foi removido.</p>
          <button
            onClick={() => navigate('/perfil?tab=negocios')}
            className="bg-[var(--primary)] text-white px-6 py-2 rounded-lg font-medium"
          >
            Voltar para Meus Negócios
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] pb-32">
      {/* Header */}
      <div className="bg-white border-b border-[var(--border)] sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/perfil?tab=negocios')}
              className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)]"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <h1 className="text-lg font-bold">Editar Negócio</h1>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-[var(--primary)] text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              data-testid="save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Step Indicator */}
        <StepIndicator
          currentStep={currentStep}
          totalSteps={11}
          stepLabels={stepLabels}
          onStepClick={(step) => setCurrentStep(step)}
        />

        {/* Step Content */}
        <div className="bg-white rounded-2xl border border-[var(--border)] p-6">
          
          {/* Step 1: Informações Básicas */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Building className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Informações Básicas</h2>
              </div>

              <FormInput
                label="CNPJ ou CPF"
                value={businessForm.cnpj_cpf}
                onChange={(e) => setBusinessForm({ ...businessForm, cnpj_cpf: e.target.value })}
                placeholder="00.000.000/0000-00"
              />

              <FormInput
                label="Nome do Negócio"
                required
                value={businessForm.nome}
                onChange={(e) => setBusinessForm({ ...businessForm, nome: e.target.value })}
                placeholder="Nome oficial"
                data-testid="business-name-input"
              />

              <FormInput
                label="Nome Fantasia"
                value={businessForm.nome_fantasia}
                onChange={(e) => setBusinessForm({ ...businessForm, nome_fantasia: e.target.value })}
                placeholder="Como é conhecido"
              />

              <FormTextarea
                label="Descrição Curta"
                value={businessForm.descricao_curta}
                onChange={(e) => setBusinessForm({ ...businessForm, descricao_curta: e.target.value })}
                placeholder="Resumo em até 250 caracteres"
                maxLength={250}
                rows={2}
              />

              <div>
                <label className="block text-sm font-medium mb-2">
                  Descrição Completa <span className="text-red-500">*</span>
                </label>
                <RichTextEditor
                  value={businessForm.descricao}
                  onChange={(html) => setBusinessForm({ ...businessForm, descricao: html })}
                  testId="edit-business-descricao-rich"
                />
                <p className="text-xs text-[var(--text-muted)] mt-2">
                  Descreva produtos, serviços, diferenciais e experiência. Mínimo recomendado: 2 parágrafos.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Categoria Principal *</label>
                <select
                  value={businessForm.categoria}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  data-testid="category-select"
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
              </div>

              {subcategorias.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Subcategoria <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={businessForm.subcategoria}
                    onChange={(e) => setBusinessForm({ ...businessForm, subcategoria: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)]"
                    required
                    data-testid="edit-business-subcategoria-select"
                  >
                    <option value="">Selecione uma subcategoria</option>
                    {subcategorias.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-[var(--text-secondary)]">
                    Ajuda clientes a encontrar seu negócio em buscas específicas.
                  </p>
                </div>
              )}

              {businessForm.categoria && businessForm.subcategoria && (
                <EspecializacoesAutocomplete
                  subcategoriaId={`${businessForm.categoria}__${(businessForm.subcategoria || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                  categoriaId={businessForm.categoria}
                  valueControladas={businessForm.tags_controladas_ids || []}
                  valueLivres={businessForm.tags_livres_pendentes || []}
                  onChangeControladas={(ids) => setBusinessForm(prev => ({ ...prev, tags_controladas_ids: ids }))}
                  onChangeLivres={(livres) => setBusinessForm(prev => ({ ...prev, tags_livres_pendentes: livres }))}
                  maxControladas={8}
                  maxLivres={3}
                  api={apiClient}
                  testIdPrefix="edit-business-esp"
                />
              )}

              <FormInput
                label="Ano de Fundação"
                type="number"
                value={businessForm.ano_fundacao}
                onChange={(e) => setBusinessForm({ ...businessForm, ano_fundacao: e.target.value })}
                placeholder="Ex: 2020"
                min="1900"
                max={new Date().getFullYear()}
              />
            </div>
          )}

          {/* Step 2: Perfil Econômico */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Briefcase className="w-6 h-6 text-[var(--primary)]" />
                <div>
                  <h2 className="text-xl font-bold">Perfil Econômico</h2>
                  <p className="text-sm text-[var(--text-secondary)]">Informações para políticas públicas</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Essas informações ajudam o município a entender melhor o perfil dos empreendedores.
                </p>
              </div>

              {businessForm.cnpj_cpf.replace(/\D/g, '').length === 14 ? (
                // Campos para negócios com CNPJ
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Porte do Negócio</h4>
                  <div className="space-y-2">
                    {[
                      { value: 'mei', label: 'MEI - Microempreendedor Individual' },
                      { value: 'microempresa', label: 'Microempresa (ME)' },
                      { value: 'epp', label: 'Empresa de Pequeno Porte (EPP)' },
                      { value: 'media', label: 'Média Empresa' },
                      { value: 'grande', label: 'Grande Empresa' }
                    ].map(option => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          businessForm.tipo_empresa === option.value
                            ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="tipo_empresa"
                          value={option.value}
                          checked={businessForm.tipo_empresa === option.value}
                          onChange={(e) => setBusinessForm({ ...businessForm, tipo_empresa: e.target.value, tem_cnpj: true })}
                          className="w-5 h-5 text-[var(--primary)]"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                // Campos para negócios informais
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Situação de Renda</h4>
                    <div className="space-y-2">
                      {[
                        { value: 'principal', label: 'Minha principal fonte de renda' },
                        { value: 'complemento', label: 'Um complemento de renda' }
                      ].map(option => (
                        <label
                          key={option.value}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            businessForm.situacao_renda === option.value
                              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="situacao_renda"
                            value={option.value}
                            checked={businessForm.situacao_renda === option.value}
                            onChange={(e) => setBusinessForm({ ...businessForm, situacao_renda: e.target.value, tem_cnpj: false })}
                            className="w-5 h-5 text-[var(--primary)]"
                          />
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Situação Ocupacional</h4>
                    <div className="space-y-2">
                      {[
                        { value: 'emprego_formal', label: 'Tenho emprego formal' },
                        { value: 'aposentado', label: 'Sou aposentado(a)' },
                        { value: 'sem_emprego', label: 'Não tenho emprego formal atualmente' },
                        { value: 'outro', label: 'Outro' }
                      ].map(option => (
                        <label
                          key={option.value}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            businessForm.situacao_ocupacao === option.value
                              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="situacao_ocupacao"
                            value={option.value}
                            checked={businessForm.situacao_ocupacao === option.value}
                            onChange={(e) => setBusinessForm({ ...businessForm, situacao_ocupacao: e.target.value })}
                            className="w-5 h-5 text-[var(--primary)]"
                          />
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {businessForm.situacao_renda === 'principal' && (
                    <>
                      <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Colaboradores Diretos</h4>
                        <div className="grid grid-cols-3 gap-2">
                          {['0', '1', '2-3', '4-5', '6-10', '10+'].map(option => (
                            <label
                              key={option}
                              className={`flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                businessForm.colaboradores_diretos === option
                                  ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="colaboradores_diretos"
                                value={option}
                                checked={businessForm.colaboradores_diretos === option}
                                onChange={(e) => setBusinessForm({ ...businessForm, colaboradores_diretos: e.target.value })}
                                className="sr-only"
                              />
                              <span className="text-sm font-medium">{option === '0' ? 'Nenhum' : option === '10+' ? 'Mais de 10' : option}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Colaboradores Indiretos</h4>
                        <div className="grid grid-cols-3 gap-2">
                          {['0', '1', '2-3', '4-5', '6-10', '10+'].map(option => (
                            <label
                              key={option}
                              className={`flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                businessForm.colaboradores_indiretos === option
                                  ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="colaboradores_indiretos"
                                value={option}
                                checked={businessForm.colaboradores_indiretos === option}
                                onChange={(e) => setBusinessForm({ ...businessForm, colaboradores_indiretos: e.target.value })}
                                className="sr-only"
                              />
                              <span className="text-sm font-medium">{option === '0' ? 'Nenhum' : option === '10+' ? 'Mais de 10' : option}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Contratação Sazonal</h4>
                        <div className="flex gap-3 mb-3">
                          {[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }].map(option => (
                            <label
                              key={String(option.value)}
                              className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                businessForm.contratacao_sazonal === option.value
                                  ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="contratacao_sazonal"
                                checked={businessForm.contratacao_sazonal === option.value}
                                onChange={() => setBusinessForm({ ...businessForm, contratacao_sazonal: option.value })}
                                className="sr-only"
                              />
                              <span className="text-sm font-medium">{option.label}</span>
                            </label>
                          ))}
                        </div>
                        {businessForm.contratacao_sazonal && (
                          <FormInput
                            label="Períodos de contratação"
                            value={businessForm.periodos_sazonais}
                            onChange={(e) => setBusinessForm({ ...businessForm, periodos_sazonais: e.target.value })}
                            placeholder="Ex: Festas de fim de ano, Carnaval..."
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Como Atende aos Clientes */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Como Atende aos Clientes</h2>
              </div>

              <p className="text-[var(--text-secondary)]">Selecione as formas de atendimento do seu negócio</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CheckboxItem id="loja_fisica" label="Loja física" checked={businessForm.loja_fisica} onChange={(e) => setBusinessForm({ ...businessForm, loja_fisica: e.target.checked })} icon="🏪" />
                <CheckboxItem id="atendimento_no_meu_local" label="Atendimento no meu local" checked={businessForm.atendimento_no_meu_local} onChange={(e) => setBusinessForm({ ...businessForm, atendimento_no_meu_local: e.target.checked })} icon="🏠" />
                <CheckboxItem id="atendimento_no_local_cliente" label="Atendimento no local do cliente" checked={businessForm.atendimento_no_local_cliente} onChange={(e) => setBusinessForm({ ...businessForm, atendimento_no_local_cliente: e.target.checked })} icon="📍" />
                <CheckboxItem id="atendimento_com_agendamento" label="Atendimento com agendamento" checked={businessForm.atendimento_com_agendamento} onChange={(e) => setBusinessForm({ ...businessForm, atendimento_com_agendamento: e.target.checked })} icon="📅" />
                <CheckboxItem id="atendimento_online" label="Atendimento online" checked={businessForm.atendimento_online} onChange={(e) => setBusinessForm({ ...businessForm, atendimento_online: e.target.checked })} icon="💻" />
                <CheckboxItem id="delivery" label="Delivery" checked={businessForm.delivery} onChange={(e) => setBusinessForm({ ...businessForm, delivery: e.target.checked })} icon="🛵" />
                <CheckboxItem id="retirada_no_local" label="Retirada no local" checked={businessForm.retirada_no_local} onChange={(e) => setBusinessForm({ ...businessForm, retirada_no_local: e.target.checked })} icon="🛍️" />
                <CheckboxItem id="entrega_combinada" label="Entrega combinada" checked={businessForm.entrega_combinada} onChange={(e) => setBusinessForm({ ...businessForm, entrega_combinada: e.target.checked })} icon="🤝" />
                <CheckboxItem id="itinerante" label="Atendimento itinerante ou ambulante" checked={businessForm.itinerante} onChange={(e) => setBusinessForm({ ...businessForm, itinerante: e.target.checked })} icon="🚚" />
              </div>

              <FormInput
                label="Área de Cobertura"
                value={businessForm.area_cobertura}
                onChange={(e) => setBusinessForm({ ...businessForm, area_cobertura: e.target.value })}
                placeholder="Ex: Centro, Bairro X, Cidade Y"
              />

              {/* Ocultar endereço */}
              <div className={`border-2 rounded-xl p-4 transition-all ${businessForm.ocultar_endereco ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer" htmlFor="ocultar_endereco_edit">
                  <input
                    type="checkbox"
                    id="ocultar_endereco_edit"
                    checked={businessForm.ocultar_endereco}
                    onChange={(e) => setBusinessForm({ ...businessForm, ocultar_endereco: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded accent-[var(--primary)]"
                    data-testid="ocultar-endereco-checkbox"
                  />
                  <div>
                    <span className="font-medium text-sm">Não desejo exibir meu endereço publicamente</span>
                    <p className="text-xs text-gray-500 mt-1">
                      Apenas a geolocalização do bairro será exibida no site. O endereço completo ficará oculto.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Step 4: Contato & Digital */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Phone className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Contato & Presença Digital</h2>
              </div>

              <FormInput
                label="WhatsApp"
                type="tel"
                required
                value={businessForm.whatsapp}
                onChange={(e) => setBusinessForm({ ...businessForm, whatsapp: e.target.value })}
                placeholder="5531999999999"
                data-testid="whatsapp-input"
              />

              <FormInput
                label="Telefone Fixo"
                type="tel"
                value={businessForm.telefone_fixo}
                onChange={(e) => setBusinessForm({ ...businessForm, telefone_fixo: e.target.value })}
                placeholder="(31) 3333-3333"
              />

              <FormInput
                label="E-mail"
                type="email"
                value={businessForm.email}
                onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                placeholder="contato@seunegocio.com"
              />

              <FormInput
                label="Site"
                type="url"
                value={businessForm.site}
                onChange={(e) => setBusinessForm({ ...businessForm, site: e.target.value })}
                placeholder="https://www.seunegocio.com"
              />

              <FormInput
                label="Instagram"
                value={businessForm.instagram}
                onChange={(e) => setBusinessForm({ ...businessForm, instagram: e.target.value })}
                placeholder="@seunegocio"
              />

              <FormInput
                label="Loja Online / Marketplace"
                type="url"
                value={businessForm.loja_online}
                onChange={(e) => setBusinessForm({ ...businessForm, loja_online: e.target.value })}
                placeholder="https://loja.seunegocio.com"
              />
            </div>
          )}

          {/* Step 5: Horários */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Horário de Funcionamento</h2>
              </div>

              <div className="space-y-3">
                {businessForm.horarios.map((horario, index) => (
                  <div key={horario.dia} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="w-24 font-medium capitalize">{horario.dia}</span>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={horario.fechado}
                        onChange={(e) => {
                          const newHorarios = [...businessForm.horarios];
                          newHorarios[index].fechado = e.target.checked;
                          setBusinessForm({ ...businessForm, horarios: newHorarios });
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">Fechado</span>
                    </label>
                    {!horario.fechado && (
                      <>
                        <input
                          type="time"
                          value={horario.abre}
                          onChange={(e) => {
                            const newHorarios = [...businessForm.horarios];
                            newHorarios[index].abre = e.target.value;
                            setBusinessForm({ ...businessForm, horarios: newHorarios });
                          }}
                          className="px-2 py-1 border rounded"
                        />
                        <span>às</span>
                        <input
                          type="time"
                          value={horario.fecha}
                          onChange={(e) => {
                            const newHorarios = [...businessForm.horarios];
                            newHorarios[index].fecha = e.target.value;
                            setBusinessForm({ ...businessForm, horarios: newHorarios });
                          }}
                          className="px-2 py-1 border rounded"
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <CheckboxItem
                  id="apenas_agendamento"
                  label="Apenas com Agendamento"
                  checked={businessForm.apenas_agendamento}
                  onChange={(e) => setBusinessForm({ ...businessForm, apenas_agendamento: e.target.checked })}
                  icon="📅"
                />
                <CheckboxItem
                  id="emergencial_24h"
                  label="Atendimento 24h / Emergencial"
                  checked={businessForm.emergencial_24h}
                  onChange={(e) => setBusinessForm({ ...businessForm, emergencial_24h: e.target.checked })}
                  icon="🚨"
                />
              </div>

              <FormTextarea
                label="Horários Especiais"
                value={businessForm.horarios_especiais}
                onChange={(e) => setBusinessForm({ ...businessForm, horarios_especiais: e.target.value })}
                placeholder="Ex: Feriados, datas especiais..."
                rows={2}
              />
            </div>
          )}

          {/* Step 6: Localização */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Localização</h2>
              </div>

              {/* 1. Municipality selection */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Município/Cidade <span className="text-red-500">*</span>
                </label>
                <select
                  value={businessForm.municipio_id}
                  onChange={(e) => handleMunicipioChange(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20"
                  data-testid="municipio-select"
                >
                  <option value="">Selecione o município</option>
                  {municipios.map(mun => (
                    <option key={mun.id} value={mun.id}>{mun.nome}</option>
                  ))}
                </select>
              </div>

              {/* 2. Sede vs Distrito */}
              {businessForm.municipio_id && territorios.filter(t => t.tipo === 'distrito').length > 0 && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium">
                    Onde está localizado? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleTerritoryTypeChange(true)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        businessForm.is_sede === true
                          ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="sede-option"
                    >
                      <Building className="w-6 h-6 mx-auto mb-2 text-[var(--primary)]" />
                      <p className="font-medium">Sede</p>
                      <p className="text-xs text-gray-500">Área urbana central</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTerritoryTypeChange(false)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        businessForm.is_sede === false
                          ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="distrito-option"
                    >
                      <MapPin className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                      <p className="font-medium">Distrito</p>
                      <p className="text-xs text-gray-500">Área do distrito</p>
                    </button>
                  </div>
                </div>
              )}

              {/* 3. District selection */}
              {businessForm.is_sede === false && territorios.filter(t => t.tipo === 'distrito').length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Selecione o Distrito <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={businessForm.distrito_id}
                    onChange={(e) => handleDistritoChange(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20"
                    data-testid="distrito-select"
                  >
                    <option value="">Selecione o distrito</option>
                    {territorios.filter(t => t.tipo === 'distrito').map(dist => (
                      <option key={dist.id} value={dist.id}>{dist.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* 4. Bairro/Comunidade selection */}
              {bairrosTerritorios.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Bairro / Comunidade / Localidade
                  </label>
                  <select
                    value={businessForm.territory_id}
                    onChange={(e) => handleTerritorySelect(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20"
                    data-testid="bairro-territory-select"
                  >
                    <option value="">Selecione ou digite abaixo</option>
                    {bairrosTerritorios.map(bairro => (
                      <option key={bairro.id} value={bairro.id}>
                        {bairro.nome} {bairro.tipo !== 'bairro' ? `(${bairro.tipo === 'comunidade' ? 'Comunidade' : bairro.tipo === 'localidade_rural' ? 'Localidade Rural' : bairro.tipo})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* 5. Manual bairro fallback */}
              <FormInput
                label={bairrosTerritorios.length > 0 ? "Ou digite o bairro/localidade" : "Bairro"}
                required={!businessForm.territory_id}
                value={businessForm.bairro}
                onChange={(e) => setBusinessForm({ ...businessForm, bairro: e.target.value })}
                placeholder="Nome do bairro"
                data-testid="neighborhood-input"
              />

              {/* 6. Street autocomplete + Geocoding */}
              <CepInput
                value={businessForm.cep}
                onChange={(v) => setBusinessForm((prev) => ({ ...prev, cep: v }))}
                onAutoFill={({ logradouro, bairro }) => {
                  setBusinessForm((prev) => ({
                    ...prev,
                    rua: logradouro || prev.rua,
                    bairro: bairro || prev.bairro,
                  }));
                }}
                required
                testId="edit-business-cep-input"
              />

              <AddressInput
                bairroId={businessForm.territory_id}
                bairroNome={businessForm.bairro}
                municipioNome={municipios.find(m => m.id === businessForm.municipio_id)?.nome || ''}
                municipioId={businessForm.municipio_id}
                cep={businessForm.cep}
                rua={businessForm.rua}
                numero={businessForm.numero}
                complemento={businessForm.complemento}
                latitude={businessForm.latitude}
                longitude={businessForm.longitude}
                onChangeRua={(v) => setBusinessForm({ ...businessForm, rua: v })}
                onChangeNumero={(v) => setBusinessForm({ ...businessForm, numero: v })}
                onChangeComplemento={(v) => setBusinessForm({ ...businessForm, complemento: v })}
                onChangeCoords={({latitude, longitude}) => setBusinessForm({ ...businessForm, latitude, longitude })}
              />
            </div>
          )}

          {/* Step 7: Comodidades */}
          {currentStep === 7 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Comodidades</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CheckboxItem id="acessibilidade" label="Acessível para Cadeirantes" checked={businessForm.acessibilidade} onChange={(e) => setBusinessForm({ ...businessForm, acessibilidade: e.target.checked })} icon="♿" />
                <CheckboxItem id="banheiro_publico" label="Banheiro Público" checked={businessForm.banheiro_publico} onChange={(e) => setBusinessForm({ ...businessForm, banheiro_publico: e.target.checked })} icon="🚻" />
                <CheckboxItem id="wifi_gratis" label="Wi-Fi Grátis" checked={businessForm.wifi_gratis} onChange={(e) => setBusinessForm({ ...businessForm, wifi_gratis: e.target.checked })} icon="📶" />
                <CheckboxItem id="pet_friendly" label="Pet Friendly" checked={businessForm.pet_friendly} onChange={(e) => setBusinessForm({ ...businessForm, pet_friendly: e.target.checked })} icon="🐕" />
                <CheckboxItem id="espaco_kids" label="Espaço Kids" checked={businessForm.espaco_kids} onChange={(e) => setBusinessForm({ ...businessForm, espaco_kids: e.target.checked })} icon="👶" />
                <CheckboxItem id="ar_condicionado" label="Ar Condicionado" checked={businessForm.ar_condicionado} onChange={(e) => setBusinessForm({ ...businessForm, ar_condicionado: e.target.checked })} icon="❄️" />
                <CheckboxItem id="area_externa" label="Área Externa" checked={businessForm.area_externa} onChange={(e) => setBusinessForm({ ...businessForm, area_externa: e.target.checked })} icon="🌳" />
                <CheckboxItem id="seguranca" label="Segurança/Monitoramento" checked={businessForm.seguranca} onChange={(e) => setBusinessForm({ ...businessForm, seguranca: e.target.checked })} icon="🔒" />
                <CheckboxItem id="entrega" label="Faz Entrega" checked={businessForm.entrega} onChange={(e) => setBusinessForm({ ...businessForm, entrega: e.target.checked })} icon="🚚" />
                <CheckboxItem id="atende_domicilio" label="Atende em Domicílio" checked={businessForm.atende_domicilio} onChange={(e) => setBusinessForm({ ...businessForm, atende_domicilio: e.target.checked })} icon="🏠" />
              </div>

              {businessForm.entrega && (
                <FormInput
                  label="Link App de Delivery"
                  type="url"
                  value={businessForm.link_delivery}
                  onChange={(e) => setBusinessForm({ ...businessForm, link_delivery: e.target.value })}
                  placeholder="https://ifood.com.br/seunegocio"
                />
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Estacionamento</label>
                <select
                  value={businessForm.estacionamento}
                  onChange={(e) => setBusinessForm({ ...businessForm, estacionamento: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[var(--border)]"
                >
                  <option value="">Não possui</option>
                  <option value="proprio">Próprio</option>
                  <option value="conveniado">Conveniado</option>
                  <option value="rua">Na rua</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Faixa de Preço</label>
                <div className="flex gap-3">
                  {[
                    { value: '$', label: '$ Econômico' },
                    { value: '$$', label: '$$ Moderado' },
                    { value: '$$$', label: '$$$ Premium' }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setBusinessForm({ ...businessForm, faixa_preco: option.value })}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all ${
                        businessForm.faixa_preco === option.value
                          ? 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]'
                          : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 8: Pagamentos */}
          {currentStep === 8 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Formas de Pagamento</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CheckboxItem id="aceita_dinheiro" label="Dinheiro" checked={businessForm.aceita_dinheiro} onChange={(e) => setBusinessForm({ ...businessForm, aceita_dinheiro: e.target.checked })} icon="💵" />
                <CheckboxItem id="aceita_cartao_credito" label="Cartão de Crédito" checked={businessForm.aceita_cartao_credito} onChange={(e) => setBusinessForm({ ...businessForm, aceita_cartao_credito: e.target.checked })} icon="💳" />
                <CheckboxItem id="aceita_cartao_debito" label="Cartão de Débito" checked={businessForm.aceita_cartao_debito} onChange={(e) => setBusinessForm({ ...businessForm, aceita_cartao_debito: e.target.checked })} icon="💳" />
                <CheckboxItem id="aceita_pix" label="PIX" checked={businessForm.aceita_pix} onChange={(e) => setBusinessForm({ ...businessForm, aceita_pix: e.target.checked })} icon="📱" />
                <CheckboxItem id="aceita_convenios" label="Convênios de Saúde" checked={businessForm.aceita_convenios} onChange={(e) => setBusinessForm({ ...businessForm, aceita_convenios: e.target.checked })} icon="🏥" />
              </div>

              {businessForm.aceita_convenios && (
                <FormInput
                  label="Quais convênios?"
                  value={businessForm.convenios_descricao}
                  onChange={(e) => setBusinessForm({ ...businessForm, convenios_descricao: e.target.value })}
                  placeholder="Ex: Unimed, Bradesco Saúde, Sulamerica"
                />
              )}
            </div>
          )}

          {/* Step 9: Diferenciais */}
          {currentStep === 9 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Diferenciais</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CheckboxItem id="producao_artesanal" label="Produção Artesanal/Local" checked={businessForm.producao_artesanal} onChange={(e) => setBusinessForm({ ...businessForm, producao_artesanal: e.target.checked })} icon="🎨" />
                <CheckboxItem id="produtos_sustentaveis" label="Produtos Sustentáveis" checked={businessForm.produtos_sustentaveis} onChange={(e) => setBusinessForm({ ...businessForm, produtos_sustentaveis: e.target.checked })} icon="🌱" />
                <CheckboxItem id="cooperativa" label="Pertence a Cooperativa" checked={businessForm.cooperativa} onChange={(e) => setBusinessForm({ ...businessForm, cooperativa: e.target.checked })} icon="🤝" />
                <CheckboxItem id="empresa_familiar" label="Empresa Familiar" checked={businessForm.empresa_familiar} onChange={(e) => setBusinessForm({ ...businessForm, empresa_familiar: e.target.checked })} icon="👨‍👩‍👧‍👦" />
              </div>
            </div>
          )}

          {/* Step 10: Social & Impacto */}
          {currentStep === 10 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Impacto Social</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CheckboxItem id="projetos_sociais" label="Participa de Projetos Sociais" checked={businessForm.projetos_sociais} onChange={(e) => setBusinessForm({ ...businessForm, projetos_sociais: e.target.checked })} icon="💝" />
                <CheckboxItem id="emprega_comunidade" label="Emprega Mão de Obra Local" checked={businessForm.emprega_comunidade} onChange={(e) => setBusinessForm({ ...businessForm, emprega_comunidade: e.target.checked })} icon="👷" />
                <CheckboxItem id="trabalha_inclusao" label="Trabalha com Inclusão" checked={businessForm.trabalha_inclusao} onChange={(e) => setBusinessForm({ ...businessForm, trabalha_inclusao: e.target.checked })} icon="♿" />
                <CheckboxItem id="apoia_cultura" label="Apoia Artistas/Cultura Local" checked={businessForm.apoia_cultura} onChange={(e) => setBusinessForm({ ...businessForm, apoia_cultura: e.target.checked })} icon="🎭" />
              </div>
            </div>
          )}

          {/* Step 11: Mídia */}
          {currentStep === 11 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <Camera className="w-6 h-6 text-[var(--primary)]" />
                <h2 className="text-xl font-bold">Imagens do Negócio</h2>
              </div>
              <p className="text-sm text-slate-600">
                Faça upload do <strong>logo</strong> do seu negócio e adicione fotos à <strong>galeria</strong>.
                Você pode adicionar, remover ou substituir as imagens a qualquer momento.
              </p>

              {/* Logo — quadrado, único. Fallback: legacy avatar_url shown as a virtual logo */}
              <ImageUploader
                area="negocio"
                tipo="logo"
                formato="quadrado"
                entidadeId={id}
                municipioId={businessForm.municipio_id}
                imagensAtuais={
                  businessForm.imagens?.logo
                    ? businessForm.imagens.logo
                    : (businessForm.avatar_url
                        ? { thumb: businessForm.avatar_url, medium: businessForm.avatar_url, large: businessForm.avatar_url, legacy: true }
                        : null)
                }
                single
                label="Logo"
                description="Imagem quadrada (ex: 1080x1080) — será exibida em destaques e listagens"
                onChange={(imgs) => {
                  setBusinessForm(prev => ({
                    ...prev,
                    imagens: { ...prev.imagens, logo: imgs[0] || null },
                    // When removing logo (or the legacy avatar fallback), clear avatar_url as well
                    avatar_url: imgs.length === 0 ? '' : (imgs[0]?.medium || prev.avatar_url)
                  }));
                }}
              />

              {/* Galeria — múltiplas, formato livre */}
              <ImageUploader
                area="negocio"
                tipo="galeria"
                formato="livre"
                entidadeId={id}
                municipioId={businessForm.municipio_id}
                imagensAtuais={businessForm.imagens?.galeria || []}
                label="Galeria de fotos"
                description="Qualquer formato — paisagem, retrato ou quadrado. As proporções originais serão preservadas."
                onChange={(imgs) => {
                  setBusinessForm(prev => ({
                    ...prev,
                    imagens: { ...prev.imagens, galeria: imgs }
                  }));
                }}
              />
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6 mb-8">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-6 py-3 rounded-xl border border-[var(--border)] font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            data-testid="prev-step-button"
          >
            <ChevronLeft className="w-5 h-5" />
            Anterior
          </button>

          {currentStep < 11 ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[var(--primary)] text-white font-medium hover:bg-[var(--primary)]/90"
              data-testid="next-step-button"
            >
              Próximo
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50"
              data-testid="save-final-button"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Salvar Alterações
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
